
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, User, ShoppingBag, Loader2 } from "lucide-react";
import { FormFieldWithValidation } from "@/components/auth/FormFieldWithValidation";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { ForgotPasswordModal } from "@/components/auth/ForgotPasswordModal";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";
import { EmailVerificationPrompt } from "@/components/auth/EmailVerificationPrompt";
import { TrustIndicators } from "@/components/auth/TrustIndicators";
import { motion } from "framer-motion";

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    confirmPassword: ""
  });
  const { toast } = useToast();

  // Auto-focus management
  useEffect(() => {
    const timer = setTimeout(() => {
      const emailInput = document.querySelector('#signin-email') as HTMLInputElement;
      if (emailInput) {
        emailInput.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateSignUp = () => {
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match. Please check and try again.",
        variant: "destructive"
      });
      return false;
    }

    if (formData.password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const logAuthAttempt = async (email: string, success: boolean, failureReason?: string) => {
    try {
      await supabase.from('login_attempts').insert({
        email,
        success,
        failure_reason: failureReason || null,
        ip_address: null, // Will be populated by trigger
        user_agent: navigator.userAgent,
        location_info: {}
      });
    } catch (error) {
      console.error('Failed to log auth attempt:', error);
    }
  };

  const handleAccountLockout = async (email: string) => {
    try {
      // Since we don't have the handle_failed_login function, we'll just log the failed attempt
      // and implement a simple client-side lockout check
      const { data: recentAttempts } = await supabase
        .from('login_attempts')
        .select('*')
        .eq('email', email)
        .eq('success', false)
        .gte('attempted_at', new Date(Date.now() - 30 * 60 * 1000).toISOString()) // Last 30 minutes
        .order('attempted_at', { ascending: false });

      if (recentAttempts && recentAttempts.length >= 5) {
        toast({
          title: "Account temporarily locked",
          description: "Too many failed login attempts. Please try again in 30 minutes.",
          variant: "destructive"
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking failed attempts:', error);
      return false;
    }
  };

  const resetFailedAttempts = async (userId: string) => {
    try {
      // Since we don't have the reset function, we'll just log a successful attempt
      // User successfully logged in
    } catch (error) {
      console.error('Error logging successful login:', error);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSignUp()) return;

    setIsLoading(true);
    
    try {
      // Starting signup process
      
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        console.error('Signup error:', error);
        throw error;
      }

      // Signup successful
      await logAuthAttempt(formData.email, true);

      if (data.user && !data.session) {
        // Email verification required
        console.log('Email verification required for user:', data.user.id);
        setVerificationEmail(formData.email);
        setShowEmailVerification(true);
        toast({
          title: "Account created! 📧",
          description: "Please check your email to verify your account.",
        });
      } else if (data.user && data.session) {
        // Auto-signed in (email verification disabled)
        console.log('User auto-signed in after signup:', data.user.id);
        await resetFailedAttempts(data.user.id);
        
        toast({
          title: "Account created successfully! 🎉",
          description: "Welcome to VRAM! You're now signed in.",
        });
        
        // Add a small delay to ensure auth context is stable before redirect
        setTimeout(() => {
          window.location.href = "/";
        }, 1000);
      }

      // Reset form
      setFormData({
        email: "",
        password: "",
        fullName: "",
        confirmPassword: ""
      });
    } catch (error: any) {
      console.error('Signup failed:', error);
      await logAuthAttempt(formData.email, false, error.message);
      
      let errorMessage = "Failed to create account";
      
      if (error.message?.includes("already registered") || error.message?.includes("User already registered")) {
        errorMessage = "An account with this email already exists. Try signing in instead.";
      } else if (error.message?.includes("Password")) {
        errorMessage = "Password requirements not met. Please choose a stronger password.";
      } else if (error.message?.includes("Email")) {
        errorMessage = "Please enter a valid email address.";
      } else if (error.message?.includes("weak password")) {
        errorMessage = "Password is too weak. Please choose a stronger password with at least 8 characters.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Check for account lockout before attempting login
      const isLocked = await handleAccountLockout(formData.email);
      if (isLocked) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) throw error;

      await logAuthAttempt(formData.email, true);

      if (data.user) {
        // Reset failed login attempts on successful login
        await resetFailedAttempts(data.user.id);
        
        toast({
          title: "Welcome back! 👋",
          description: "You have been signed in successfully.",
        });
        // Redirect to home page
        window.location.href = "/";
      }
    } catch (error: any) {
      await logAuthAttempt(formData.email, false, error.message);
      
      let errorMessage = "Failed to sign in";
      
      if (error.message?.includes("Invalid login credentials")) {
        errorMessage = "Invalid email or password. Please check your credentials and try again.";
      } else if (error.message?.includes("Email not confirmed")) {
        errorMessage = "Please check your email and click the verification link before signing in.";
        setVerificationEmail(formData.email);
        setShowEmailVerification(true);
      } else if (error.message?.includes("too many")) {
        errorMessage = "Too many login attempts. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Sign in failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <EmailVerificationPrompt
          email={verificationEmail}
          onBack={() => {
            setShowEmailVerification(false);
            setVerificationEmail("");
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 dark:from-background dark:via-background dark:to-primary/10 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
      <motion.div 
        className="w-full max-w-md sm:max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="text-center mb-6 sm:mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary to-primary/60 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20">
              <ShoppingBag className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-2">VRAM</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Premium tech products with worldwide shipping</p>
          <p className="text-xs text-muted-foreground mt-1.5 hidden sm:block">Free shipping on orders over $50 • 30-day returns • Expert support</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Card className="shadow-2xl border-border/60 backdrop-blur-sm bg-card/95">
            <CardHeader className="space-y-2 px-4 sm:px-6 pt-6 pb-4">
              <CardTitle className="text-xl sm:text-2xl text-foreground">Get Started</CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Sign in to your account or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-6">
              <Tabs defaultValue="signin" className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-11">
                  <TabsTrigger value="signin" className="transition-all duration-200 text-sm sm:text-base">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" className="transition-all duration-200 text-sm sm:text-base">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin">
                  <motion.div 
                    className="space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                     <div className="mb-2">
                       <p className="text-sm text-muted-foreground text-center">
                         Welcome back! Access your orders, wishlist, and exclusive deals
                       </p>
                     </div>

                     <GoogleAuthButton mode="signin" />
                     
                     <div className="relative">
                       <div className="absolute inset-0 flex items-center">
                         <Separator className="w-full" />
                       </div>
                       <div className="relative flex justify-center text-xs uppercase">
                         <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                       </div>
                     </div>

                    <form onSubmit={handleSignIn} className="space-y-4">
                      <FormFieldWithValidation
                        id="signin-email"
                        name="email"
                        type="email"
                        label="Email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleInputChange}
                        icon={<Mail />}
                        validation={{
                          required: true,
                          email: true,
                        }}
                        autoFocus
                      />
                      
                      <FormFieldWithValidation
                        id="signin-password"
                        name="password"
                        type="password"
                        label="Password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleInputChange}
                        icon={<Lock />}
                        validation={{
                          required: true,
                        }}
                        showPasswordToggle
                      />

                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-primary hover:text-primary/80 transition-colors"
                        >
                          Forgot your password?
                        </button>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full h-11 sm:h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300" 
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Signing in...</span>
                          </div>
                        ) : (
                          "Sign In"
                        )}
                      </Button>
                    </form>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="signup">
                  <motion.div 
                    className="space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="mb-2">
                      <p className="text-sm text-muted-foreground text-center">
                        Create your account in 30 seconds and start shopping premium tech
                      </p>
                    </div>

                    <GoogleAuthButton mode="signup" />
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                      </div>
                    </div>

                    <form onSubmit={handleSignUp} className="space-y-4">
                      <FormFieldWithValidation
                        id="signup-name"
                        name="fullName"
                        type="text"
                        label="Full Name"
                        placeholder="Enter your full name"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        icon={<User />}
                        validation={{
                          required: true,
                          minLength: 2,
                        }}
                      />
                      
                      <FormFieldWithValidation
                        id="signup-email"
                        name="email"
                        type="email"
                        label="Email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleInputChange}
                        icon={<Mail />}
                        validation={{
                          required: true,
                          email: true,
                        }}
                      />
                      
                      <div className="space-y-2">
                        <FormFieldWithValidation
                          id="signup-password"
                          name="password"
                          type="password"
                          label="Password"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleInputChange}
                          icon={<Lock />}
                          validation={{
                            required: true,
                            minLength: 8,
                          }}
                          showPasswordToggle
                        />
                        <PasswordStrengthIndicator password={formData.password} />
                      </div>
                      
                      <FormFieldWithValidation
                        id="confirm-password"
                        name="confirmPassword"
                        type="password"
                        label="Confirm Password"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        icon={<Lock />}
                        validation={{
                          required: true,
                          customValidator: (value) => 
                            value !== formData.password ? "Passwords do not match" : null
                        }}
                        showPasswordToggle
                      />
                      
                      <Button 
                        type="submit" 
                        className="w-full h-11 sm:h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300" 
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Creating account...</span>
                          </div>
                        ) : (
                          "Create Account"
                        )}
                      </Button>
                    </form>
                  </motion.div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Trust indicators */}
          <TrustIndicators />
        </motion.div>
      </motion.div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default Auth;
